export type GenericObject = { [key: string]: any };

export const removeDuplicates = <T extends GenericObject>(
  data: T[] | undefined, // Allow data to be undefined
  keys: (keyof T)[],
): T[] => {
  // Guard clause: Return an empty array if data is undefined or not an array
  if (!Array.isArray(data)) {
    return [];
  }

  return [...new Map(data.map((item) => [keys.map((key) => item[key]).join('|'), item])).values()];
};

export const buildTree = <
  T extends { id: string; parentID: string; children?: T[]; level?: number },
>(
  items: any[],
): T[] => {
  const map: { [key: string]: T[] } = {};
  const tree: T[] = [];

  // Step 1: Create map with parentID as key and array of children as value
  items.forEach((item) => {
    if (!item.parentID) return;

    if (!map[item.parentID]) {
      map[item.parentID] = [item];
      return;
    }
    map[item.parentID] = [...map[item.parentID], item];
  });

  // Step 2: Recursive function to build children tree
  const buildChildren = (parentId: string): T[] => {
    const children = map[parentId] || [];
    return children.map((child) => ({
      ...child,
      children: buildChildren(child.id),
    }));
  };

  // Step 3: Build tree starting from root items (items without parentID)
  const rootItems = items.filter((item) => item.level === 1);
  rootItems.forEach((rootItem) => {
    tree.push({
      ...rootItem,
      children: buildChildren(rootItem.id),
    });
  });

  return tree;
};
